import {
	CHART_SCORE_SCALE_DEFAULTS,
	MAX_AXIS_TICKS,
	MAX_CHART_AXIS_VALUE,
} from "@klarify/constants/charts";
import type { ChartConfig } from "@klarify/schemas";

// Y-axis tick derivation for yTickInterval / yTickLabels. Recharts computes
// its own ticks when these are unset; explicit ticks keep the client, the
// export, and the mirrored axis identical.

export interface DerivedYAxis {
	domain?: [number, number];
	ticks?: number[];
}

/** Tolerance for float drift when a value sits on a tick. */
const TICK_EPSILON = 1e-6;

// Bars encode value as length, so a scale-derived axis must keep the zero
// baseline; a T-score bar chart starting at 30 exaggerates differences.
const ZERO_BASELINE_CHART_TYPES = new Set<ChartConfig["chartType"]>([
	"bar",
	"horizontalBar",
	"radialBar",
]);

function scaleDefaults(config: ChartConfig) {
	return config.scoreScale
		? CHART_SCORE_SCALE_DEFAULTS[config.scoreScale]
		: null;
}

function roundTick(value: number): number {
	return Number(value.toPrecision(12));
}

/** A finite value inside ±MAX_CHART_AXIS_VALUE; anything else is not data. */
function plottable(value: number): number | null {
	return Number.isFinite(value) && Math.abs(value) <= MAX_CHART_AXIS_VALUE
		? value
		: null;
}

/** chartConfigSchema.data allows numeric strings, so "97" counts as 97. */
function numericValue(value: unknown): number | null {
	if (typeof value === "number") {
		return plottable(value);
	}
	if (typeof value === "string" && value.trim() !== "") {
		return plottable(Number(value));
	}
	return null;
}

/** [min, max] of the plotted values, error whiskers included. */
export function dataExtent(config: ChartConfig): [number, number] | null {
	let min = Number.POSITIVE_INFINITY;
	let max = Number.NEGATIVE_INFINITY;
	for (const row of config.data) {
		for (const yKey of config.yKeys) {
			const value = numericValue(row[yKey.key]);
			if (value === null) {
				continue;
			}
			const spread = yKey.errorKey
				? (numericValue(row[yKey.errorKey]) ?? 0)
				: 0;
			const low =
				yKey.errorLowKey === undefined
					? value - spread
					: (numericValue(row[yKey.errorLowKey]) ?? value);
			const high =
				yKey.errorHighKey === undefined
					? value + spread
					: (numericValue(row[yKey.errorHighKey]) ?? value);
			min = Math.min(min, low);
			max = Math.max(max, high);
		}
	}
	if (!(Number.isFinite(min) && Number.isFinite(max))) {
		return null;
	}
	return [min, max];
}

/**
 * [min, max] of the reference lines and bands drawn on the y axis. These
 * are not data (they do not decide a chart's score scale) but the axis
 * must hold them too, or a "clinically significant" band gets cut.
 */
export function annotationExtent(config: ChartConfig): [number, number] | null {
	const values: number[] = [];
	for (const line of config.referenceLines ?? []) {
		const y = numericValue(line.y);
		if (y !== null) {
			values.push(y);
		}
	}
	for (const band of config.referenceBands ?? []) {
		for (const edge of [band.y1, band.y2]) {
			const y = numericValue(edge);
			if (y !== null) {
				values.push(y);
			}
		}
	}
	return values.length > 0 ? [Math.min(...values), Math.max(...values)] : null;
}

function unionExtent(
	a: [number, number] | null,
	b: [number, number] | null,
): [number, number] | null {
	if (!a) {
		return b;
	}
	if (!b) {
		return a;
	}
	return [Math.min(a[0], b[0]), Math.max(a[1], b[1])];
}

/**
 * Everything the y axis must hold: the plotted values plus every reference
 * line and band. Recharts drops (does not clip) an annotation outside the
 * axis, so every synthesized domain starts from this extent, never from
 * the data alone. Pass `extent` when the caller already computed
 * dataExtent(config).
 */
export function coveredExtent(
	config: ChartConfig,
	extent: [number, number] | null = dataExtent(config),
): [number, number] | null {
	return unionExtent(extent, annotationExtent(config));
}

function ascending(domain: readonly [number, number]): [number, number] {
	return [Math.min(domain[0], domain[1]), Math.max(domain[0], domain[1])];
}

/** True when every plotted value (the extent) lies inside the domain. */
export function extentWithin(
	extent: [number, number] | null,
	domain: readonly [number, number],
): boolean {
	return extent !== null && extent[0] >= domain[0] && extent[1] <= domain[1];
}

/**
 * The value the tick grid is anchored on. Normal-linear scales (those with
 * an sd) tick at mean ± k·interval, so a standard-score axis ticks at the
 * SD bounds (40, 55, 70, 85, 100, ...) and a scaled-score axis at 1, 4, 7,
 * ..., 19. Percentile ranks and unscaled charts tick at interval multiples,
 * so an explicit [5, 100] by 10 ticks at 10, 20, ..., 100.
 */
function tickAnchor(config: ChartConfig): number {
	const defaults = scaleDefaults(config);
	return defaults && defaults.sd !== null && defaults.mean !== null
		? defaults.mean
		: 0;
}

function snapDown(value: number, step: number, anchor: number): number {
	return roundTick(
		anchor + Math.floor((value - anchor) / step + TICK_EPSILON) * step,
	);
}

function snapUp(value: number, step: number, anchor: number): number {
	return roundTick(
		anchor + Math.ceil((value - anchor) / step - TICK_EPSILON) * step,
	);
}

function niceStep(span: number, targetTicks: number): number {
	const rough = span / Math.max(1, targetTicks);
	const magnitude = 10 ** Math.floor(Math.log10(Math.max(rough, 1e-9)));
	const normalized = rough / magnitude;
	if (normalized < 1.5) {
		return magnitude;
	}
	if (normalized < 3) {
		return 2 * magnitude;
	}
	if (normalized < 7) {
		return 5 * magnitude;
	}
	return 10 * magnitude;
}

/**
 * The value domain a config pins, always ascending: its explicit yDomain,
 * else the score scale's default (percentile ranks 0–100, T scores 30–90,
 * ...) widened to the nearest tick that holds every plotted value,
 * reference line, and band, so nothing is clipped. Bar-type charts also
 * keep the zero baseline. Undefined leaves recharts to derive the axis
 * from the data — which overshoots (a percentile axis to 120), hence the
 * scale default. An unscaled chart gets a domain only when an annotation
 * falls outside its data; recharts would drop that band, not clip it.
 * Pass `extent` when the caller already computed dataExtent(config).
 */
export function resolveYDomain(
	config: ChartConfig,
	extent: [number, number] | null = dataExtent(config),
): [number, number] | undefined {
	if (config.yDomain) {
		return ascending(config.yDomain);
	}
	const defaults = scaleDefaults(config);
	const covered = coveredExtent(config, extent);
	if (!defaults?.yDomain) {
		if (!covered || (extent && extentWithin(covered, extent))) {
			return undefined;
		}
		return unscaledDomain(config, covered);
	}
	const [min, max] = defaults.yDomain;
	const baselineMin = ZERO_BASELINE_CHART_TYPES.has(config.chartType)
		? Math.min(min, 0)
		: min;
	if (!covered || extentWithin(covered, [baselineMin, max])) {
		return [baselineMin, max];
	}
	// The widened bounds stay on the scale's tick grid.
	const step =
		config.yTickInterval ?? defaults.yTickInterval ?? (max - min) / 4;
	const anchor = tickAnchor(config);
	return [
		Math.min(baselineMin, snapDown(Math.min(min, covered[0]), step, anchor)),
		snapUp(Math.max(max, covered[1]), step, anchor),
	];
}

/** The covered extent rounded out to a nice grid, as recharts would round the data. */
function unscaledDomain(
	config: ChartConfig,
	covered: [number, number],
): [number, number] {
	const step =
		config.yTickInterval && config.yTickInterval > 0
			? config.yTickInterval
			: niceStep(covered[1] - covered[0] || 1, 5);
	const min = snapDown(covered[0], step, 0);
	return [
		ZERO_BASELINE_CHART_TYPES.has(config.chartType) ? Math.min(min, 0) : min,
		snapUp(covered[1], step, 0),
	];
}

/**
 * Derive an explicit domain + tick list when the config (or its score
 * scale) pins a tick interval. Without one, recharts defaults apply
 * (undefined) — unless `forceTicks` is set, which synthesizes a nice
 * interval. A mirrored chart sets it and passes the ONE result to both
 * axes; recharts would otherwise scale each axis from its own bound
 * series and the two sides would disagree.
 */
export function deriveYAxis(
	config: ChartConfig,
	options: { forceTicks?: boolean } = {},
): DerivedYAxis {
	const defaults = scaleDefaults(config);
	const extent = dataExtent(config);
	const domain = resolveYDomain(config, extent);
	// Without a pinned domain the axis is synthesized from the covered
	// extent, so a band outside the data still widens it.
	const bounds = domain ?? coveredExtent(config, extent) ?? [0, 100];
	let interval = config.yTickInterval ?? defaults?.yTickInterval ?? undefined;
	if ((!interval || interval <= 0) && options.forceTicks) {
		interval = niceStep(bounds[1] - bounds[0] || 1, 5);
	}
	if (!interval || interval <= 0) {
		return { domain };
	}
	const anchor = tickAnchor(config);
	let [min, max] = bounds;
	if (!domain) {
		min = snapDown(min, interval, anchor);
		max = snapUp(max, interval, anchor);
		if (min === max) {
			max = min + interval;
		}
	}
	// A tick list past MAX_AXIS_TICKS is unreadable and, on a 1e9 domain,
	// takes seconds to build; the interval was a bad fit for this domain,
	// so recharts picks the ticks while the domain still holds.
	const expectedTicks = (max - min) / interval;
	if (!Number.isFinite(expectedTicks) || expectedTicks > MAX_AXIS_TICKS) {
		return { domain: [min, max] };
	}
	// Ticks are the grid points inside the domain; an unaligned explicit
	// domain end is not a tick (see tickAnchor). Multiplying instead of
	// accumulating keeps every tick exact and the loop finite.
	const first = snapUp(min, interval, anchor);
	const ticks: number[] = [];
	for (let index = 0; index <= MAX_AXIS_TICKS; index += 1) {
		const tick = roundTick(first + index * interval);
		if (tick > max + interval * TICK_EPSILON) {
			break;
		}
		ticks.push(tick);
	}
	// An explicit domain narrower than the interval ([61, 69] by 10) holds
	// no grid point; an empty tick list would draw an axis with no labels.
	return ticks.length > 0
		? { domain: [min, max], ticks }
		: { domain: [min, max] };
}

/** Tick formatter honoring yTickLabels overrides ("200" → "≤ 200"). */
export function formatYTick(
	config: ChartConfig,
	value: number | string,
): string {
	const raw = String(value);
	return config.yTickLabels?.[raw] ?? raw;
}
